// src/sections/BillingDetails.jsx — the company's invoicing record (route
// /billing-details), reached from the avatar menu.
//
// ⭐ MOVED here from /credits on 2026-09-24. It was never wallet content: this
// is the company's invoicing IDENTITY (legal name, VAT, SDI/PEC, address). The
// backend split the same content out of its credits guide on 2026-09-04.
//
// ⛔ WHICH customer? Resolved from the ACTIVE TENANT, because `customers[]` is
// an ARRAY — one person may represent two companies. /credits never had to
// choose: it passed `balance.customer_id`, which the BE computes as
// `get_tenant_customer_id(active_tenant)`. A standalone page has no workspace
// context, so it asks /credits/balance for the same answer. ⭐ With exactly one
// `role === 'admin'` entry the answer is unambiguous and the read is skipped.
//
// ⛔ THIS PAGE STAYS, and is NOT a redirect to the "Dati azienda" tab of
// /tenant-admin/manage (2026-09-26): that tree is behind RequireWorkspaceAdmin,
// so a company admin whose administered workspaces are not the active one — or
// who administers none — would be bounced away from their own invoicing record.
// The tab and this page therefore share only the BODY (CompanyBillingBody) and
// resolve the company each its own way: the tab from the console's company
// selector, this page from the active workspace, as described above.
//
// ⚠️ The gate lives in the body (a PRE-GATE only): `CustomerBillingForm` hides
// itself on a 403 CUSTOMER_ADMIN_REQUIRED (which is also what an unknown id
// returns — the gate precedes the lookup), and the server remains the real
// authority.
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { fetchCreditBalance } from '../services/api/creditsApi.js';
import CompanyBillingBody from '../components/credits/CompanyBillingBody.jsx';

export default function BillingDetails() {
  const { isSuperadmin, adminMode, activeTenant, customers } = useAuth();
  const tenantId = activeTenant?.tenant_id;

  // The single-admin fast path: unambiguous, so no network read is needed.
  const adminEntries = (customers || []).filter(c => c.role === 'admin');
  const soleAdminId = adminEntries.length === 1 ? adminEntries[0].customer_id : null;

  // Only the ambiguous path stores anything: the id the active workspace resolves to.
  // The sole-admin id is DERIVED on every render rather than seeded into state, so a
  // claims refresh that changes it is picked up instead of freezing the first value.
  const [resolvedId, setResolvedId] = useState(null);

  useEffect(() => {
    // Only the ambiguous case costs a read: several admin entries, so the
    // active workspace decides which company this page is about.
    if (soleAdminId || !tenantId || adminEntries.length === 0) return undefined;
    let cancelled = false;
    setResolvedId(null); // never show the previous workspace's company while resolving
    fetchCreditBalance({ tenant: tenantId })
      .then((b) => { if (!cancelled) setResolvedId(b?.customer_id || null); })
      .catch(() => { if (!cancelled) setResolvedId(null); });
    return () => { cancelled = true; };
  }, [soleAdminId, tenantId, adminEntries.length]);

  if (isSuperadmin && adminMode) return <Navigate to="/admin/users" replace />;

  return <CompanyBillingBody customerId={soleAdminId || resolvedId} />;
}
